import React, { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Search, Building2, Workflow, AlertTriangle, Lightbulb, FileText, ChevronRight, Check, Download, ArrowRight, BarChart3, Zap, Shield, TrendingUp } from 'lucide-react';
type Step = 'input' | 'analyzing' | 'preview' | 'capture' | 'delivered';
const analysisSteps = [{
  label: 'Scanning website',
  icon: Search,
  duration: 1200
}, {
  label: 'Identifying industry',
  icon: Building2,
  duration: 1000
}, {
  label: 'Mapping workflows',
  icon: Workflow,
  duration: 1400
}, {
  label: 'Detecting inefficiencies',
  icon: AlertTriangle,
  duration: 1100
}, {
  label: 'Generating AI use cases',
  icon: Lightbulb,
  duration: 1300
}, {
  label: 'Building report',
  icon: FileText,
  duration: 1000
}];
// Fake report data for the preview
const reportSections = [{
  title: 'Industry Classification',
  value: 'Hospitality & Tourism',
  icon: Building2
}, {
  title: 'Automation Potential',
  value: '73% of workflows',
  icon: TrendingUp
}, {
  title: 'Priority Use Cases',
  value: '8 identified',
  icon: Zap
}, {
  title: 'Estimated Efficiency Gain',
  value: '40–60%',
  icon: BarChart3
}, {
  title: 'Compliance Risk Areas',
  value: '3 flagged',
  icon: Shield
}];
const useCases = ['Automated guest inquiry handling', 'Intelligent reservation management', 'Proactive service recovery', 'Dynamic upsell orchestration', 'Compliance document automation', 'Real-time operational reporting', 'Staff scheduling optimisation', 'Multi-channel communication routing'];
export function OpportunityReport() {
  const [step, setStep] = useState<Step>('input');
  const [url, setUrl] = useState('');
  const [analysisIndex, setAnalysisIndex] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startAnalysis = useCallback(() => {
    if (!url.trim()) return;
    setStep('analyzing');
    setAnalysisIndex(0);
    let totalDelay = 0;
    analysisSteps.forEach((s, index) => {
      totalDelay += s.duration;
      setTimeout(() => {
        setAnalysisIndex(index + 1);
        if (index === analysisSteps.length - 1) {
          setTimeout(() => setStep('preview'), 800);
        }
      }, totalDelay);
    });
  }, [url]);
  const handleSubmit = useCallback(() => {
    if (!name.trim() || !email.trim()) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setStep('delivered');
    }, 1500);
  }, [name, email]);
  const handleReset = useCallback(() => {
    setStep('input');
    setUrl('');
    setAnalysisIndex(0);
    setName('');
    setEmail('');
    setCompany('');
  }, []);
  return <section className="py-32 bg-[#050505] relative z-10 border-t border-white/5 overflow-hidden" data-id="element-374">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-brand-orange/5 rounded-full blur-[150px] pointer-events-none" data-id="element-375" />

      <div className="max-w-5xl mx-auto px-6 relative z-10" data-id="element-376">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-377">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-orange/10 border border-brand-orange/20 text-xs font-medium text-brand-orange uppercase tracking-widest mb-6" data-id="element-378">
            <Zap className="w-3 h-3" data-id="element-379" />
            Free Analysis
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-white mb-6 tracking-tight" data-id="element-380">
            Get Your AI{' '}
            <span className="text-gray-500" data-id="element-381">Opportunity Report.</span>
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-382">
            Enter your website and our agent will analyse your business, map
            workflows, and generate a tailored AI opportunity report.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto" data-id="element-383">
          <AnimatePresence mode="wait" data-id="element-384">
            {/* INPUT STATE */}
            {step === 'input' && <motion.div key="input" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.5
          }} className="flex flex-col items-center" data-id="element-385">
                <div className="w-full bg-[#0A0A0A] border border-white/10 rounded-2xl p-2 flex items-center gap-2 shadow-2xl" data-id="element-386">
                  <div className="flex items-center gap-3 pl-4 flex-1" data-id="element-387">
                    <Globe className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-388" />
                    <input type="url" value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && startAnalysis()} placeholder="Enter your website URL" className="flex-1 bg-transparent text-white text-lg placeholder:text-gray-600 focus:outline-none py-4" data-id="element-389" />
                  </div>
                  <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={startAnalysis} disabled={!url.trim()} className="px-8 py-4 bg-brand-orange hover:bg-brand-deep-orange disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all duration-300 flex items-center gap-2 shadow-[0_0_20px_rgba(219,127,74,0.2)] hover:shadow-[0_0_30px_rgba(219,127,74,0.4)] flex-shrink-0" data-id="element-390">
                    Generate Report
                    <ArrowRight className="w-4 h-4" data-id="element-391" />
                  </motion.button>
                </div>

                <p className="text-xs text-gray-600 mt-4" data-id="element-392">
                  No signup required. Results in under 30 seconds.
                </p>
              </motion.div>}

            {/* ANALYZING STATE */}
            {step === 'analyzing' && <motion.div key="analyzing" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.5
          }} className="flex flex-col items-center" data-id="element-393">
                <div className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-10 w-full shadow-2xl" data-id="element-394">
                  {/* URL being analyzed */}
                  <div className="flex items-center gap-3 mb-8 pb-6 border-b border-white/5" data-id="element-395">
                    <Globe className="w-4 h-4 text-brand-orange" data-id="element-396" />
                    <span className="text-sm font-mono text-gray-400 truncate" data-id="element-397">
                      {url}
                    </span>
                    <motion.div className="ml-auto w-2 h-2 rounded-full bg-brand-orange" animate={{
                  opacity: [1, 0.3, 1]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity
                }} data-id="element-398" />
                  </div>

                  <div className="space-y-5" data-id="element-399">
                    {analysisSteps.map((s, index) => {
                  const isActive = analysisIndex === index + 1;
                  const isCompleted = analysisIndex > index + 1;
                  const isPending = analysisIndex <= index;
                  return <motion.div key={index} initial={{
                    opacity: 0.2
                  }} animate={{
                    opacity: isActive || isCompleted ? 1 : 0.2
                  }} transition={{
                    duration: 0.4
                  }} className="flex items-center gap-5" data-id="element-400">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 ${isActive ? 'bg-brand-orange/20 border border-brand-orange/50 shadow-[0_0_12px_rgba(219,127,74,0.3)]' : isCompleted ? 'bg-white/10 border border-white/20' : 'bg-[#111] border border-white/5'}`} data-id="element-401">
                            {isCompleted ? <Check className="w-4 h-4 text-gray-400" data-id="element-402" /> : <s.icon className={`w-4 h-4 ${isActive ? 'text-brand-orange' : 'text-gray-700'}`} data-id="element-403" />}
                            {isActive && <motion.div className="absolute inset-0 rounded-xl border border-brand-orange" animate={{
                        scale: [1, 1.15, 1],
                        opacity: [1, 0, 1]
                      }} transition={{
                        duration: 1.5,
                        repeat: Infinity
                      }} data-id="element-404" />}
                          </div>

                          <span className={`text-sm font-medium transition-colors duration-500 ${isActive ? 'text-white' : isCompleted ? 'text-gray-400' : 'text-gray-700'}`} data-id="element-405">
                            {s.label}
                          </span>

                          {isActive && <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: '100%'
                    }} transition={{
                      duration: s.duration / 1000,
                      ease: 'linear'
                    }} className="flex-1 h-px bg-gradient-to-r from-brand-orange/50 to-transparent" data-id="element-406" />}
                        </motion.div>;
                })}
                  </div>
                </div>
              </motion.div>}

            {/* PREVIEW STATE (with lead capture overlay) */}
            {(step === 'preview' || step === 'capture') && <motion.div key="preview" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.6
          }} className="relative" data-id="element-407">
                {/* Blurred Report Preview */}
                <div className={`bg-[#0A0A0A] border border-white/10 rounded-3xl p-10 w-full shadow-2xl transition-all duration-500 ${step === 'capture' ? 'blur-md scale-[0.98] opacity-50' : 'blur-[2px]'}`} data-id="element-408">
                  {/* Report Header */}
                  <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/5" data-id="element-409">
                    <div data-id="element-410">
                      <p className="text-xs font-mono text-brand-orange uppercase tracking-widest mb-1" data-id="element-411">
                        AI Opportunity Report
                      </p>
                      <p className="text-sm text-gray-500 font-mono truncate max-w-[300px]" data-id="element-412">
                        {url}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-413">
                      <p className="text-xs text-gray-600" data-id="element-414">Generated by</p>
                      <p className="text-sm font-display text-white" data-id="element-415">
                        Aijentik
                      </p>
                    </div>
                  </div>

                  {/* Report Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-id="element-416">
                    {reportSections.map((section, i) => <div key={i} className="bg-[#111] border border-white/5 rounded-xl p-5" data-id="element-417">
                        <section.icon className="w-4 h-4 text-brand-orange mb-3" data-id="element-418" />
                        <p className="text-xs text-gray-500 mb-1" data-id="element-419">
                          {section.title}
                        </p>
                        <p className="text-lg font-medium text-white" data-id="element-420">
                          {section.value}
                        </p>
                      </div>)}
                  </div>

                  {/* Use Cases Preview */}
                  <div data-id="element-421">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-4" data-id="element-422">
                      Priority AI Use Cases
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-423">
                      {useCases.map((uc, i) => <div key={i} className="flex items-center gap-3 text-sm text-gray-300" data-id="element-424">
                          <div className="w-1.5 h-1.5 rounded-full bg-brand-orange flex-shrink-0" data-id="element-425" />
                          {uc}
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Glass Overlay / Lead Capture */}
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: step === 'preview' ? 0.5 : 0,
              duration: 0.5
            }} className="absolute inset-0 flex items-center justify-center z-20" data-id="element-426">
                  <AnimatePresence mode="wait" data-id="element-427">
                    {step === 'preview' && <motion.div key="unlock" initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} transition={{
                  duration: 0.4
                }} className="bg-[#0A0A0A]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-10 text-center max-w-sm shadow-2xl" data-id="element-428">
                        <div className="w-14 h-14 rounded-full bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center mx-auto mb-6" data-id="element-429">
                          <FileText className="w-6 h-6 text-brand-orange" data-id="element-430" />
                        </div>
                        <h3 className="font-display text-2xl text-white mb-3 tracking-tight" data-id="element-431">
                          Your report is ready.
                        </h3>
                        <p className="text-sm text-gray-400 mb-8" data-id="element-432">
                          Enter your details to unlock the full AI Opportunity
                          Report.
                        </p>
                        <motion.button whileHover={{
                    scale: 1.02
                  }} whileTap={{
                    scale: 0.98
                  }} onClick={() => setStep('capture')} className="w-full px-6 py-4 bg-brand-orange hover:bg-brand-deep-orange text-white font-medium rounded-xl transition-colors duration-300 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(219,127,74,0.2)]" data-id="element-433">
                          Unlock Report
                          <ChevronRight className="w-4 h-4" data-id="element-434" />
                        </motion.button>
                      </motion.div>}

                    {step === 'capture' && <motion.div key="capture" initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} transition={{
                  duration: 0.4
                }} className="bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 rounded-2xl p-10 max-w-sm w-full shadow-2xl" data-id="element-435">
                        <h3 className="font-display text-xl text-white mb-6 tracking-tight text-center" data-id="element-436">
                          Almost there.
                        </h3>
                        <div className="space-y-4 mb-8" data-id="element-437">
                          <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} className="w-full bg-[#111] border border-white/10 rounded-xl px-5 py-4 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-brand-orange/50 transition-colors" data-id="element-438" />
                          <input type="email" placeholder="Work email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-[#111] border border-white/10 rounded-xl px-5 py-4 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-brand-orange/50 transition-colors" data-id="element-439" />
                          <input type="text" placeholder="Company (optional)" value={company} onChange={e => setCompany(e.target.value)} className="w-full bg-[#111] border border-white/10 rounded-xl px-5 py-4 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-brand-orange/50 transition-colors" data-id="element-440" />
                        </div>
                        <motion.button whileHover={{
                    scale: 1.02
                  }} whileTap={{
                    scale: 0.98
                  }} onClick={handleSubmit} disabled={!name.trim() || !email.trim() || isSubmitting} className="w-full px-6 py-4 bg-brand-orange hover:bg-brand-deep-orange disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(219,127,74,0.2)]" data-id="element-441">
                          {isSubmitting ? <motion.div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" animate={{
                      rotate: 360
                    }} transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: 'linear'
                    }} data-id="element-442" /> : <>
                              Get My Report
                              <Download className="w-4 h-4" data-id="element-443" />
                            </>}
                        </motion.button>
                        <button onClick={() => setStep('preview')} className="w-full text-center text-xs text-gray-600 hover:text-gray-400 transition-colors mt-4" data-id="element-444">
                          Back
                        </button>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>
              </motion.div>}

            {/* DELIVERED STATE */}
            {step === 'delivered' && <motion.div key="delivered" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.6
          }} className="flex flex-col items-center text-center" data-id="element-445">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              duration: 0.5,
              type: 'spring',
              bounce: 0.3
            }} className="w-20 h-20 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-8 relative" data-id="element-446">
                  <Check className="w-8 h-8 text-green-400" data-id="element-447" />
                  <motion.div className="absolute inset-0 rounded-full bg-green-500/10" animate={{
                scale: [1, 1.5],
                opacity: [0.5, 0]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-448" />
                </motion.div>

                <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="font-display text-3xl text-white mb-4 tracking-tight" data-id="element-449">
                  Your report is on its way.
                </motion.h3>

                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.5
            }} className="text-gray-400 text-lg mb-10 max-w-md" data-id="element-450">
                  We've sent your personalised AI Opportunity Report to{' '}
                  <span className="text-white font-medium" data-id="element-451">{email}</span>.
                </motion.p>

                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.7
            }} className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 w-full max-w-sm mb-10" data-id="element-452">
                  <div className="space-y-3 text-sm" data-id="element-453">
                    <div className="flex justify-between" data-id="element-454">
                      <span className="text-gray-500" data-id="element-455">Website</span>
                      <span className="text-white font-mono text-xs truncate max-w-[200px]" data-id="element-456">
                        {url}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-457">
                      <span className="text-gray-500" data-id="element-458">Use cases found</span>
                      <span className="text-brand-orange font-medium" data-id="element-459">8</span>
                    </div>
                    <div className="flex justify-between" data-id="element-460">
                      <span className="text-gray-500" data-id="element-461">
                        Automation potential
                      </span>
                      <span className="text-brand-orange font-medium" data-id="element-462">73%</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.9
            }} className="flex flex-col sm:flex-row items-center gap-4" data-id="element-463">
                  <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="px-8 py-4 bg-brand-orange hover:bg-brand-deep-orange text-white font-medium rounded-full transition-colors duration-300 flex items-center gap-2 shadow-[0_0_20px_rgba(219,127,74,0.2)]" data-id="element-464">
                    <Download className="w-4 h-4" data-id="element-465" />
                    Download PDF
                  </motion.button>
                  <button onClick={handleReset} className="px-8 py-4 bg-white/5 border border-white/10 hover:border-white/30 text-white font-medium rounded-full transition-all duration-300 text-sm" data-id="element-466">
                    Analyse Another Site
                  </button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}